/**
 * useActivityFeed — unified, RLS-scoped feed of user-generated activity.
 *
 * Sources are real DB tables only (consultas, decodificar_express) — mock
 * adapters for roteiros/videos are excluded so the dashboard reflects what
 * the user actually did, not seeded fixtures.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { pointsFor } from "../constants/activityPoints";

export type ActivityKind = "consulta" | "decodificar" | "criativo" | "roteiro" | "simulador";
export type DashboardPeriod = "today" | "7d" | "14d" | "30d" | "3m" | "all";

export interface ActivityItem {
  id: string;
  kind: ActivityKind;
  title: string;
  description: string;
  createdAt: string;
}

interface UseActivityFeedResult {
  items: ActivityItem[];
  isLoading: boolean;
  isError: boolean;
}

const STALE_MS = 60 * 1000;

export function useActivityFeed(limit = 20): UseActivityFeedResult {
  const { client, isReady } = useSupabaseClient();
  const { user, isAuthenticated } = useCurrentUser();

  const enabled = isReady && isAuthenticated;

  const consultasQuery = useQuery({
    queryKey: ["activity-feed", "consultas", user?.id ?? "anon"],
    enabled,
    staleTime: STALE_MS,
    queryFn: async () => {
      const { data, error } = await client
        .from("consultas")
        .select("id, label, created_at")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  });

  const decodQuery = useQuery({
    queryKey: ["activity-feed", "decodificar", user?.id ?? "anon"],
    enabled,
    staleTime: STALE_MS,
    queryFn: async () => {
      const { data, error } = await client
        .from("decodificar_express")
        .select("id, patient_label, result_type, created_at")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  });

  const criativosQuery = useQuery({
    queryKey: ["activity-feed", "criativos", user?.id ?? "anon"],
    enabled,
    staleTime: STALE_MS,
    queryFn: async () => {
      const { data, error } = await client
        .from("criativos")
        .select("id, label, veiculacao, created_at")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  });

  const roteirosQuery = useQuery({
    queryKey: ["activity-feed", "roteiros", user?.id ?? "anon"],
    enabled,
    staleTime: STALE_MS,
    queryFn: async () => {
      const { data, error } = await client
        .from("ai_jobs")
        .select("id, task, status, created_at, ai_job_results(analysis_json)")
        .eq("task", "roteiros")
        .eq("status", "done")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  });

  const simuladorQuery = useQuery({
    queryKey: ["activity-feed", "simulador", user?.id ?? "anon"],
    enabled,
    staleTime: STALE_MS,
    queryFn: async () => {
      const { data, error } = await client
        .from("simulator_sessions")
        .select("id, created_at")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  });

  const items = useMemo<ActivityItem[]>(() => {
    const consultas: ActivityItem[] = (consultasQuery.data ?? []).map((c) => ({
      id: `consulta-${c.id}`,
      kind: "consulta",
      title: "Consulta analisada",
      description: c.label?.trim() || "Sem rótulo",
      createdAt: c.created_at,
    }));
    const decod: ActivityItem[] = (decodQuery.data ?? []).map((d) => ({
      id: `decod-${d.id}`,
      kind: "decodificar",
      title: "Paciente decodificado",
      description: d.patient_label?.trim()
        ? `${d.patient_label} — Código ${d.result_type}`
        : `Código ${d.result_type}`,
      createdAt: d.created_at,
    }));
    const criativos: ActivityItem[] = (criativosQuery.data ?? []).map((c) => ({
      id: `criativo-${c.id}`,
      kind: "criativo",
      title: "Criativo analisado",
      description: c.label?.trim()
        ? `${c.label} — ${c.veiculacao}`
        : c.veiculacao ?? "Análise de criativo",
      createdAt: c.created_at,
    }));
    const roteiros: ActivityItem[] = (roteirosQuery.data ?? []).map((j) => {
      const result = Array.isArray(j.ai_job_results)
        ? j.ai_job_results[0]
        : j.ai_job_results;
      const topic = (result as { analysis_json?: { topic?: string } } | null)
        ?.analysis_json?.topic;
      return {
        id: `roteiro-${j.id}`,
        kind: "roteiro",
        title: "Roteiro gerado",
        description: topic?.trim() || "Roteiro com IA",
        createdAt: j.created_at,
      };
    });
    const simulador: ActivityItem[] = (simuladorQuery.data ?? []).map((s) => ({
      id: `sim-${s.id}`,
      kind: "simulador",
      title: "Simulação realizada",
      description: "Treino no simulador",
      createdAt: s.created_at,
    }));
    return [...consultas, ...decod, ...criativos, ...roteiros, ...simulador].sort((a, b) =>
      a.createdAt < b.createdAt ? 1 : -1,
    );
  }, [
    consultasQuery.data,
    decodQuery.data,
    criativosQuery.data,
    roteirosQuery.data,
    simuladorQuery.data,
  ]);

  return {
    items,
    isLoading:
      consultasQuery.isLoading ||
      decodQuery.isLoading ||
      criativosQuery.isLoading ||
      roteirosQuery.isLoading ||
      simuladorQuery.isLoading,
    isError:
      consultasQuery.isError ||
      decodQuery.isError ||
      criativosQuery.isError ||
      roteirosQuery.isError ||
      simuladorQuery.isError,
  };
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function periodStart(now: Date, period: DashboardPeriod): Date | null {
  const start = startOfDay(now);
  if (period === "all") return null;
  if (period === "today") return start;
  if (period === "7d") {
    start.setDate(start.getDate() - 6);
    return start;
  }
  if (period === "14d") {
    start.setDate(start.getDate() - 13);
    return start;
  }
  if (period === "30d") {
    start.setDate(start.getDate() - 29);
    return start;
  }
  start.setMonth(start.getMonth() - 3);
  return start;
}

export function filterActivityByPeriod(
  items: ActivityItem[],
  period: DashboardPeriod,
): ActivityItem[] {
  const now = new Date();
  const start = periodStart(now, period);
  if (!start) return items;
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return items.filter((item) => {
    const t = new Date(item.createdAt).getTime();
    return t >= start.getTime() && t <= end.getTime();
  });
}

/**
 * Bucket activity into the last 7 days (Mon..Sun, locale-aware), returning
 * counts per day. Each item earns 5 points; total returned alongside.
 */
export interface WeeklyBucket {
  day: string;
  count: number;
  points: number;
}

export function useWeeklyProgress(): {
  buckets: WeeklyBucket[];
  totalPoints: number;
  isLoading: boolean;
} {
  const { items, isLoading } = useActivityFeed(500);
  const filtered = filterActivityByPeriod(items, "7d");

  const { buckets, totalPoints } = useMemo(() => {
    const labels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
    const counts: number[] = [0, 0, 0, 0, 0, 0, 0];
    const pts: number[]    = [0, 0, 0, 0, 0, 0, 0];

    for (const item of filtered) {
      const d = new Date(item.createdAt);
      const idx = (d.getDay() + 6) % 7;
      counts[idx] += 1;
      pts[idx]    += pointsFor(item.kind);
    }

    const buckets: WeeklyBucket[] = labels.map((day, i) => ({ day, count: counts[i], points: pts[i] }));
    const totalPoints = pts.reduce((a, b) => a + b, 0);
    return { buckets, totalPoints };
  }, [filtered]);

  return { buckets, totalPoints, isLoading };
}
